import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Restrict the route to the DEFAULT (admin) user; others are sent back to /workflows. */
  adminOnly?: boolean;
}

/**
 * ProtectedRoute Component
 *
 * Wraps routes that require authentication.
 * Redirects to login if user is not authenticated.
 *
 * With Streem authentication, the auth state is managed entirely
 * through the login flow and persisted in Zustand store.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, adminOnly = false }) => {
  const { isAuthenticated, loading, isDefaultUser } = useAuthStore();

  // Show loading spinner while checking auth state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If authenticated, render the protected content
  if (isAuthenticated) {
    // Admin-only pages (Users, Connected Services, Session Activity) bounce non-admins home.
    // The backend enforces this too — this guard just avoids landing on a page full of 403s.
    if (adminOnly && !isDefaultUser) {
      return <Navigate to="/workflows" replace />;
    }
    return <>{children}</>;
  }

  // If not authenticated, redirect to login
  return <Navigate to="/" replace />;
};

export default ProtectedRoute;
